import { useState, useRef, useEffect } from "react";
import confetti from "canvas-confetti";

const LOGO_URL = "https://i.postimg.cc/4NGK550H/pinnacle-icon.png";
const FALLBACK_KNOWLEDGE = `Knowledge base could not be loaded. Please check the /api/knowledge endpoint and ensure the service account has access to the Google Drive docs.`;

const BUILD_SYSTEM_PROMPT = (knowledge) => `You are the Pinnacle Admin AI Assistant — an expert on all Pinnacle Life Group admin processes, carrier rules, contracting, and staff contacts.

YOUR PRIMARY JOB:
When a user asks a question, find the relevant information in the knowledge base and SHOW IT TO THEM DIRECTLY. Do not just link to a document — extract and display the actual data, rules, steps, or contacts from the knowledge base in your response. Links should supplement your answer, not replace it.

EXAMPLE OF GOOD BEHAVIOR:
User asks: "wait time for NLG"
Good response: Lists the actual NLG wait time rules from the knowledge base (e.g. "6 months from last submitted policy, can't write, all debts must be paid..."), then includes the source link.
Bad response: "Here's a link to the Specialty Carrier Notes doc." — when the rules were in fact in the knowledge base and could have been quoted.

NEVER ASSEMBLE A LIST THAT THE DOCUMENTS DO NOT STATE — this is the single most important rule:
These admins act on your answers to file real carrier paperwork, so a plausible-looking invented list is far worse than no list.
- Only present a list (of carriers, forms, steps, contacts, requirements) when a document states that list. Quote what the document actually says.
- If a document gives examples — "the Equita carriers (RNA, AMAM, etc.)" — that is an EXAMPLE, NOT a complete roster. Never expand "etc." into a full list of your own making, and never turn examples into a definitive answer.
- Do NOT build a list by collecting mentions scattered across documents and grouping them yourself. Gathering every carrier that appears near the word "Equita" is exactly the error that produces wrong answers.
- If the knowledge base does not contain the list the user asked for, say so directly: "The knowledge base doesn't have a definitive list of X. Here's what the documents do say: ..." then quote the relevant passages and link the source. This is a CORRECT and expected answer, not a failure.
- Before sending, re-read your own answer. If any part of it contradicts another part — for example listing a carrier under one IMO while also stating it belongs to a different IMO — the grouping is wrong. Delete it and say what the documents actually state.
- Prefer being incomplete and correct over being complete and wrong.

ABBREVIATION & ALIAS MATCHING — always expand these before searching:
- "NLG" = National Life Group OR North American Life Group — search for both
- "MoO" / "MOO" = Mutual of Omaha
- "F&G" = Fidelity & Guaranty Life
- "AHL" = American Health & Life
- "RNA" = RNA / Reliance Standard
- "AmAm" = American Amicable
- "SureLC" = SureLC contracting platform
- "HCMS" = HCMS ticketing system
- If you don't recognize an abbreviation, still search the full knowledge base for any partial match

TOPIC ROUTING:
- Wait times / transfer waits / non-solicit / release periods → check the Wait Times document
- Contracting / new producer / SureLC → check contracting docs
- Hierarchy changes / comp changes → check AHL or hierarchy docs
- HCMS tickets → check HCMS documentation
- Staff contacts / who handles X → check staff contact docs

RESPONSE FORMAT:
1. Answer the question directly with the actual data from the knowledge base
2. Use numbered steps for processes
3. Use bullet points for lists of rules or requirements
4. Include relevant email contacts inline when applicable
5. Include relevant links inline as markdown [Link Name](https://...)
6. End with "📄 Source:" linking to the source document(s)

DOCUMENT FRESHNESS — this matters, admins edit these documents constantly:
- Every section header carries an "UPDATED:" timestamp — the last time that document was edited in Google Drive.
- If two documents contradict each other, trust the one with the more recent UPDATED timestamp, use it for your answer, and say which document you used and when it was last updated.
- If a document is marked "[TRUNCATED ...]" or "[NOT INCLUDED ...]", you are only seeing part of it. Answer with what you can see, then tell the user plainly that the document was too large to load in full and link them to the SOURCE so they can check the rest. Never guess at the missing part.
- If a section says the document could not be read from Google Drive, say so and link the user to it rather than implying the information does not exist.

FALLBACK — when the knowledge base genuinely does not answer the question:
"I wasn't able to find that information in the Pinnacle knowledge base. Please contact support for further assistance."
Use this rather than guessing. If you found related-but-not-exact information, share that instead and be explicit about what you could not find.

KNOWLEDGE BASE:
${knowledge}`;

// How often the app quietly re-checks Drive for edits while it is open.
// The check is cheap on the server (metadata only) and is a no-op unless a
// document actually changed.
const AUTO_REVALIDATE_MS = 5 * 60 * 1000;

function formatAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const DEFAULT_SUGGESTIONS = [
  "How do I release an agent from Pinnacle?",
  "What are the RNA comp change rules?",
  "Who handles Medicare contracting?",
  "How do I complete AML training?",
  "How do I do an AHL hierarchy change?",
  "How do I add a non-resident license for Foresters?",
];

const ROBOT_MESSAGES = [
  "You've got this!",
  "Great questions!",
  "I'm here to help!",
  "Keep going!",
  "Ask me anything!",
  "You're an awesome admin!"
];

function parseMarkdown(text, linkColor) {
  return text
    .replace(/^### (.*$)/gm, "<strong>$1</strong>")
    .replace(/^## (.*$)/gm, "<strong style='font-size:15px;'>$1</strong>")
    .replace(/^# (.*$)/gm, "<strong style='font-size:16px;'>$1</strong>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g, `<a href="$2" target="_blank" rel="noopener noreferrer" style="color:${linkColor};text-decoration:underline;word-break:break-word;">$1</a>`)
    .replace(/\[([^\]]+)\]\((mailto:[^\)]+)\)/g, `<a href="$2" style="color:${linkColor};text-decoration:underline;">$1</a>`)
    .replace(/^- (.*$)/gm, "<span style='display:block;padding-left:12px;'>• $1</span>")
    .replace(/^(\d+)\.\s/gm, "<span style='font-weight:600;'>$1.</span> ")
    .replace(/\n/g, "<br/>");
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

export default function App() {
  const [dark, setDark] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "👋 Hi! I'm the **Pinnacle Admin Assistant**. Loading your latest knowledge base from Google Drive..." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS);
  const [knowledge, setKnowledge] = useState(null);
  const [kbStatus, setKbStatus] = useState("loading");
  const [kbInfo, setKbInfo] = useState(null);
  const [, setClockTick] = useState(0);
  const kbFingerprintRef = useRef(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshToast, setRefreshToast] = useState(null);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportToast, setReportToast] = useState(null);
  const [readyPill, setReadyPill] = useState(false);
  const [robotMessageIndex, setRobotMessageIndex] = useState(0);
  const prevKbStatusRef = useRef(null);
  const messagesEndRef = useRef(null);

  // ── Design tokens ──────────────────────────────────────────────────────────
  const d = dark;
  const t = {
    // gradient background — lighter dark so orbs shine through
    gradientDark:  "linear-gradient(135deg, #1c2236 0%, #1a2744 25%, #1e1c38 50%, #231830 75%, #1c2236 100%)",
    gradientLight: "linear-gradient(135deg, #eef2ff 0%, #fdf4ff 25%, #f0fdf4 50%, #fff7ed 75%, #eef2ff 100%)",

    // orbs — more vivid in dark so they're visible against lighter base
    orb1: d ? "radial-gradient(circle, rgba(59,130,246,0.22) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(147,197,253,0.40) 0%, transparent 65%)",
    orb2: d ? "radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(216,180,254,0.38) 0%, transparent 65%)",
    orb3: d ? "radial-gradient(circle, rgba(16,185,129,0.14) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(167,243,208,0.45) 0%, transparent 65%)",

    // chrome
    headerBg:      d ? "rgba(28,34,54,0.78)"   : "rgba(255,255,255,0.75)",
    headerBorder:  d ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)",
    inputAreaBg:   d ? "rgba(28,34,54,0.82)"   : "rgba(249,250,251,0.88)",
    inputBoxBg:    d ? "rgba(38,46,70,0.96)"   : "#ffffff",
    inputBoxBorder:d ? "rgba(255,255,255,0.12)" : "#e5e7eb",

    // bubbles
    aiBubbleBg:    d ? "rgba(38,46,70,0.88)"   : "rgba(255,255,255,0.92)",
    aiBubbleBorder:d ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)",
    userBubble:    d ? "#3b6fd4"               : "#1d4ed8",

    // text — noticeably brighter in dark
    titleColor:    d ? "#f8fafc"  : "#111827",
    subtitleColor: d ? "#a8b8d0"  : "#6b7280",
    aiText:        d ? "#e8edf5"  : "#111827",
    userText:      "#ffffff",
    inputText:     d ? "#e8edf5"  : "#111827",
    placeholderC:  d ? "#5c7090"  : "#9ca3af",
    linkColor:     d ? "#7eb8f7"  : "#2563eb",
    dotColor:      d ? "#5c7090"  : "#9ca3af",

    // chips
    chipBg:        d ? "rgba(38,46,70,0.85)"   : "rgba(255,255,255,0.88)",
    chipBorder:    d ? "rgba(255,255,255,0.11)" : "#e5e7eb",
    chipText:      d ? "#c8d8ee"  : "#374151",
    freqText:      d ? "#6b82a0"  : "#9ca3af",
  };

  function applyKnowledge(data) {
    setKnowledge(data.knowledge);
    setKbInfo({
      builtAt: data.builtAt ?? null,
      totalCount: data.totalCount ?? null,
      degraded: !!data.degraded,
      warnings: data.warnings ?? [],
      truncatedFiles: data.truncatedFiles ?? [],
      omittedFiles: data.omittedFiles ?? [],
    });
    kbFingerprintRef.current = data.fingerprint ?? null;
    setKbStatus("live");
  }

  function loadKnowledge(forceRefresh = false) {
    const url = forceRefresh ? "/api/knowledge?refresh=true" : "/api/knowledge";
    return fetch(url, { cache: "no-store" })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => {
        if (!data.knowledge) throw new Error("Empty response");
        applyKnowledge(data);
        return data;
      });
  }

  useEffect(() => {
    loadKnowledge()
      .then(() => setMessages([{ role: "assistant", text: "👋 Hi! I'm the **Pinnacle Admin Assistant**. I'm all set with the latest from Google Drive — staff contacts, carrier info, HCMS tickets, SureLC, contracting, releases, transfers, AML training, and more. What can I help with?" }]))
      .catch(() => {
        setKnowledge(FALLBACK_KNOWLEDGE);
        setKbStatus("fallback");
        setMessages([{ role: "assistant", text: "👋 Hi! I'm the **Pinnacle Admin Assistant**. ⚠️ Couldn't connect to Google Drive right now — I may have outdated information. How can I help?" }]);
      });

    fetch("/api/suggestions")
      .then(r => r.json())
      .then(data => { if (data.suggestions) setSuggestions(data.suggestions); })
      .catch(() => {});
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  useEffect(() => {
    if (prevKbStatusRef.current === "loading" && kbStatus === "live") setReadyPill(true);
    prevKbStatusRef.current = kbStatus;
  }, [kbStatus]);

  useEffect(() => {
    if (!readyPill) return;
    const t = setTimeout(() => setReadyPill(false), 2500);
    return () => clearTimeout(t);
  }, [readyPill]);

  useEffect(() => {
    const id = setInterval(() => setRobotMessageIndex((i) => (i + 1) % ROBOT_MESSAGES.length), 9000);
    return () => clearInterval(id);
  }, []);

  // Keep the "Updated Xm ago" label honest without re-fetching anything.
  useEffect(() => {
    const id = setInterval(() => setClockTick(n => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Quietly pick up Drive edits while the tab is open — on an interval and
  // whenever the admin comes back to the tab. The server compares each file's
  // modifiedTime, so this is a no-op unless something actually changed.
  useEffect(() => {
    if (kbStatus !== "live") return;
    let cancelled = false;

    async function revalidate() {
      if (document.hidden || cancelled) return;
      try {
        const res = await fetch("/api/knowledge", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data.knowledge) return;
        // Unchanged documents produce an identical fingerprint — nothing to do.
        if (data.fingerprint && data.fingerprint === kbFingerprintRef.current) return;
        applyKnowledge(data);
        setRefreshToast("updated");
      } catch {
        // Silent: the admin still has a working knowledge base loaded.
      }
    }

    const id = setInterval(revalidate, AUTO_REVALIDATE_MS);
    window.addEventListener("focus", revalidate);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", revalidate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kbStatus]);

  useEffect(() => {
    if (!refreshToast) return;
    const timer = setTimeout(() => setRefreshToast(null), 3000);
    return () => clearTimeout(timer);
  }, [refreshToast]);

  useEffect(() => {
    if (!reportToast) return;
    const timer = setTimeout(() => setReportToast(null), 3000);
    return () => clearTimeout(timer);
  }, [reportToast]);

  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true); setKbStatus("loading");
    try { await loadKnowledge(true); setRefreshToast("success"); }
    catch { setKbStatus("fallback"); setRefreshToast("error"); }
    finally { setRefreshing(false); }
  }

  function closeReportModal() {
    if (!reportSubmitting) {
      setReportIssueOpen(false);
      setReportName(""); setReportEmail(""); setReportDescription("");
      setReportToast(null);
    }
  }

  async function handleReportSubmit(e) {
    e.preventDefault();
    const name = reportName.trim();
    const email = reportEmail.trim();
    const description = reportDescription.trim();
    if (!name || !email || !description) return;
    setReportSubmitting(true);
    try {
      const res = await fetch("/api/report-issue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, description }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setReportToast("success");
        setReportIssueOpen(false);
        setReportName(""); setReportEmail(""); setReportDescription("");
      } else {
        setReportToast("error");
      }
    } catch {
      setReportToast("error");
    } finally {
      setReportSubmitting(false);
    }
  }

  async function sendMessage(overrideText) {
    const userMsg = (overrideText ?? input).trim();
    if (!userMsg || loading || !knowledge) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);
    fetch("/api/track", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ question: userMsg }) }).catch(() => {});
    try {
      const history = messages.map(m => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
      const res = await fetch("/api/chat", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "anthropic/claude-haiku-4-5", max_tokens: 1500,
          messages: [{ role: "system", content: BUILD_SYSTEM_PROMPT(knowledge) }, ...history, { role: "user", content: userMsg }],
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: "assistant", text: data.choices?.[0]?.message?.content || "Sorry, I couldn't generate a response." }]);
      confetti({ particleCount: 40, spread: 55, origin: { y: 0.75 }, colors: ["#3b82f6", "#8b5cf6", "#10b981"] });
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Something went wrong. Please try again." }]);
    } finally { setLoading(false); }
  }

  const showSuggestions = messages.length === 1 && kbStatus !== "loading";
  const inputDisabled = loading || !knowledge;

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>

      {/* ── Gradient background ── */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 0,
        background: d ? t.gradientDark : t.gradientLight,
        backgroundSize: "400% 400%",
        animation: "gradientShift 14s ease infinite",
        transition: "background 0.9s ease",
      }} />

      {/* ── Orbs ── */}
      {[
        { w:620, h:620, top:"-180px", left:"-130px", bg: t.orb1, dur:"18s", delay:"0s"  },
        { w:520, h:520, bottom:"-90px", right:"-90px", bg: t.orb2, dur:"23s", delay:"2s"  },
        { w:380, h:380, top:"42%", left:"52%", bg: t.orb3, dur:"28s", delay:"5s"  },
      ].map((o, i) => (
        <div key={i} style={{
          position: "fixed", zIndex: 0, pointerEvents: "none",
          width: o.w, height: o.h, borderRadius: "50%",
          ...(o.top    ? { top:    o.top    } : {}),
          ...(o.bottom ? { bottom: o.bottom } : {}),
          ...(o.left   ? { left:   o.left   } : {}),
          ...(o.right  ? { right:  o.right  } : {}),
          background: o.bg,
          animation: `orbFloat ${o.dur} ease-in-out infinite ${o.delay}`,
          transition: "background 0.9s ease",
        }} />
      ))}

      {/* ── Header ── */}
      <div style={{
        backgroundColor: t.headerBg,
        backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
        borderBottom: `1px solid ${t.headerBorder}`,
        padding: "14px 24px", display: "flex", alignItems: "center", gap: "12px",
        position: "sticky", top: 0, zIndex: 10,
        transition: "background 0.4s, border-color 0.4s",
      }}>
        <img src={LOGO_URL} alt="Pinnacle" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain", flexShrink: 0, filter: d ? "brightness(0) invert(1)" : "none", transition: "filter 0.3s" }} />

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: t.titleColor, transition: "color 0.3s" }}>
            Pinnacle Admin Assistant
          </div>
          <div style={{ fontSize: 12, color: t.subtitleColor, display: "flex", alignItems: "center", gap: 5, transition: "color 0.3s", flexWrap: "wrap" }}>
            {kbStatus === "loading"  && <><Dot color="#f59e0b" />{refreshing ? "Refreshing knowledge base..." : "Loading knowledge base from Drive..."}</>}
            {kbStatus === "live"     && (
              <>
                <Dot color={kbInfo?.degraded ? "#f59e0b" : "#22c55e"} />
                {kbInfo?.degraded ? "Drive refresh failed · showing last known good data" : "Live from Google Drive · auto-updates when docs change"}
                {kbInfo?.builtAt && (
                  <span title={`Knowledge base built ${new Date(kbInfo.builtAt).toLocaleString()}${kbInfo.totalCount ? ` from ${kbInfo.totalCount} files` : ""}`}>
                    · updated {formatAgo(kbInfo.builtAt)}
                  </span>
                )}
              </>
            )}
            {kbStatus === "fallback" && <><Dot color="#ef4444" />Drive unavailable · running on cached data</>}
            {readyPill && (
              <span style={{
                marginLeft: 6, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
                background: d ? "rgba(34,197,94,0.25)" : "rgba(34,197,94,0.18)", color: "#22c55e",
                animation: "readyPillIn 0.25s ease",
              }}>Ready ✓</span>
            )}
          </div>
        </div>

        {/* Refresh */}
        <HeaderBtn onClick={handleRefresh} disabled={refreshing} title="Refresh knowledge base" t={t}>
          <span style={{ fontSize: 15, animation: refreshing ? "spin 1s linear infinite" : "none", display:"inline-block" }}>⟳</span>
          <span>{refreshing ? "Refreshing…" : "Refresh"}</span>
        </HeaderBtn>

        {/* Report Issue */}
        <HeaderBtn onClick={() => setReportIssueOpen(true)} disabled={false} title="If your responses don't seem right let us know" t={t}>
          <span>Report Issue</span>
        </HeaderBtn>

        {/* Theme toggle */}
        <button onClick={() => setDark(v => !v)} title={d ? "Light mode" : "Dark mode"}
          style={{
            display:"flex", alignItems:"center", justifyContent:"center",
            width:36, height:36,
            background: d ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
            border: `1px solid ${t.headerBorder}`,
            borderRadius: 8, color: t.subtitleColor, cursor: "pointer", transition: "all 0.2s", flexShrink:0,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor="#7eb8f7"; e.currentTarget.style.color="#7eb8f7"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=t.headerBorder; e.currentTarget.style.color=t.subtitleColor; }}
        >
          <span style={{ display:"flex", transition:"transform 0.45s ease", transform: d ? "rotate(0deg)" : "rotate(20deg)" }}>
            {d ? <SunIcon /> : <MoonIcon />}
          </span>
        </button>
      </div>

      {/* ── Toast ── */}
      {refreshToast && (() => {
        const ok = refreshToast === "success" || refreshToast === "updated";
        const text = {
          success: "✅ Knowledge base refreshed from Google Drive",
          updated: "🔄 Google Drive changed — knowledge base updated",
          error:   "❌ Refresh failed — please try again",
        }[refreshToast];
        return (
          <div style={{
            position:"fixed", top:70, right:20, zIndex:50,
            background: ok ? (d?"rgba(20,83,45,0.94)":"#f0fdf4") : (d?"rgba(127,29,29,0.94)":"#fef2f2"),
            border:`1px solid ${ok?"#86efac":"#fca5a5"}`,
            color: ok ? (d?"#86efac":"#166534") : (d?"#fca5a5":"#991b1b"),
            borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:500,
            backdropFilter:"blur(12px)", boxShadow:"0 4px 20px rgba(0,0,0,0.18)",
            animation:"fadeIn 0.2s ease",
          }}>
            {text}
          </div>
        );
      })()}

      {/* ── Report Issue toast ── */}
      {reportToast && (
        <div style={{
          position:"fixed", top:70, right:20, zIndex:50,
          background: reportToast==="success" ? (d?"rgba(20,83,45,0.94)":"#f0fdf4") : (d?"rgba(127,29,29,0.94)":"#fef2f2"),
          border:`1px solid ${reportToast==="success"?"#86efac":"#fca5a5"}`,
          color: reportToast==="success" ? (d?"#86efac":"#166534") : (d?"#fca5a5":"#991b1b"),
          borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:500,
          backdropFilter:"blur(12px)", boxShadow:"0 4px 20px rgba(0,0,0,0.18)",
          animation:"fadeIn 0.2s ease",
        }}>
          {reportToast==="success" ? "✅ Issue report sent. Thanks for your feedback." : "❌ Failed to send. Please try again."}
        </div>
      )}

      {/* ── Report Issue modal ── */}
      {reportIssueOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-issue-title"
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: d ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
          onClick={(e) => e.target === e.currentTarget && closeReportModal()}
        >
          <div
            style={{
              background: t.inputBoxBg,
              border: `1px solid ${t.headerBorder}`,
              borderRadius: 14,
              padding: "24px 24px 20px",
              maxWidth: 420,
              width: "100%",
              boxShadow: d ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.12)",
              transition: "background 0.3s, border-color 0.3s",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="report-issue-title" style={{ margin: "0 0 18px", fontSize: 17, fontWeight: 700, color: t.titleColor }}>
              Report an issue
            </h2>
            <form onSubmit={handleReportSubmit}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: t.subtitleColor, marginBottom: 4 }}>Name</label>
              <input
                type="text"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                required
                placeholder="Your name"
                style={{
                  width: "100%", marginBottom: 14, padding: "10px 12px",
                  background: t.chipBg, border: `1px solid ${t.chipBorder}`,
                  borderRadius: 8, fontSize: 14, color: t.inputText,
                  fontFamily: "inherit", boxSizing: "border-box",
                }}
              />
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: t.subtitleColor, marginBottom: 4 }}>Email</label>
              <input
                type="email"
                value={reportEmail}
                onChange={(e) => setReportEmail(e.target.value)}
                required
                placeholder="your@email.com"
                style={{
                  width: "100%", marginBottom: 14, padding: "10px 12px",
                  background: t.chipBg, border: `1px solid ${t.chipBorder}`,
                  borderRadius: 8, fontSize: 14, color: t.inputText,
                  fontFamily: "inherit", boxSizing: "border-box",
                }}
              />
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: t.subtitleColor, marginBottom: 4 }}>Description</label>
              <textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                required
                placeholder="Describe the issue..."
                rows={4}
                style={{
                  width: "100%", marginBottom: 18, padding: "10px 12px",
                  background: t.chipBg, border: `1px solid ${t.chipBorder}`,
                  borderRadius: 8, fontSize: 14, color: t.inputText,
                  fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={closeReportModal}
                  disabled={reportSubmitting}
                  style={{
                    padding: "8px 16px", fontSize: 13, fontWeight: 500,
                    background: "transparent", border: `1px solid ${t.headerBorder}`,
                    borderRadius: 8, color: t.subtitleColor, cursor: reportSubmitting ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reportSubmitting || !reportName.trim() || !reportEmail.trim() || !reportDescription.trim()}
                  style={{
                    padding: "8px 16px", fontSize: 13, fontWeight: 500,
                    background: "#2563eb", color: "#fff", border: "none",
                    borderRadius: 8, cursor: reportSubmitting ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    opacity: reportSubmitting ? 0.7 : 1,
                  }}
                >
                  {reportSubmitting ? "Sending…" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Chat ── */}
      <div style={{ flex:1, maxWidth:760, width:"100%", margin:"0 auto", padding:"20px 16px 130px", display:"flex", flexDirection:"column", gap:14, position:"relative", zIndex:1 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display:"flex", justifyContent: msg.role==="user"?"flex-end":"flex-start", alignItems:"flex-start", gap:8, animation: i === messages.length - 1 ? "messageIn 0.35s ease" : "none" }}>
            {msg.role==="assistant" && (
              <img src={LOGO_URL} alt="Pinnacle" style={{ width:28, height:28, borderRadius:6, objectFit:"contain", flexShrink:0, marginTop:2, background: d?"rgba(38,46,70,0.8)":"#fff", border:`1px solid ${t.aiBubbleBorder}`, filter: d ? "brightness(0) invert(1)" : "none", transition: "filter 0.3s" }} />
            )}
            <div style={{
              maxWidth:"78%", padding:"11px 15px",
              borderRadius: msg.role==="user" ? "14px 14px 4px 14px" : "4px 14px 14px 14px",
              background: msg.role==="user" ? t.userBubble : t.aiBubbleBg,
              color: msg.role==="user" ? t.userText : t.aiText,
              border: msg.role==="assistant" ? `1px solid ${t.aiBubbleBorder}` : "none",
              fontSize:14, lineHeight:1.7,
              backdropFilter: msg.role==="assistant" ? "blur(10px)" : "none",
              boxShadow: d ? "0 2px 16px rgba(0,0,0,0.28)" : "0 1px 6px rgba(0,0,0,0.07)",
              transition:"background 0.3s, color 0.3s, border-color 0.3s",
              letterSpacing: "0.01em",
            }}
              dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text, t.linkColor) }}
            />
          </div>
        ))}

        {loading && (
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <img src={LOGO_URL} alt="Pinnacle" style={{ width:28, height:28, borderRadius:6, objectFit:"contain", flexShrink:0, background: d?"rgba(38,46,70,0.8)":"#fff", border:`1px solid ${t.aiBubbleBorder}`, filter: d ? "brightness(0) invert(1)" : "none", transition: "filter 0.3s" }} />
            <div style={{ background:t.aiBubbleBg, border:`1px solid ${t.aiBubbleBorder}`, borderRadius:"4px 14px 14px 14px", padding:"13px 17px", display:"flex", gap:5, backdropFilter:"blur(10px)" }}>
              {[0,1,2].map(d => <div key={d} style={{ width:7, height:7, borderRadius:"50%", background:t.dotColor, animation:"pulse 1.2s ease-in-out infinite", animationDelay:`${d*0.2}s` }} />)}
            </div>
          </div>
        )}

        {showSuggestions && (
          <div style={{ marginTop:6 }}>
            <div style={{ fontSize:12, color:t.freqText, marginBottom:9, marginLeft:36 }}>Recently asked:</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginLeft:36 }}>
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)} style={{
                  background:t.chipBg, border:`1px solid ${t.chipBorder}`,
                  borderRadius:20, padding:"7px 15px", fontSize:13, color:t.chipText,
                  cursor:"pointer", backdropFilter:"blur(8px)", transition:"all 0.2s",
                  fontFamily:"inherit",
                  animation:"chipIn 0.3s ease both",
                  animationDelay:`${i * 0.05}s`,
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor="#7eb8f7"; e.currentTarget.style.color="#7eb8f7"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor=t.chipBorder; e.currentTarget.style.color=t.chipText; }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Robot agent ── */}
      <div
        className="robot-agent-wrap"
        style={{
          position: "fixed",
          bottom: 90,
          left: 20,
          zIndex: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            background: t.chipBg,
            border: `1px solid ${t.chipBorder}`,
            borderRadius: 12,
            padding: "8px 12px",
            marginBottom: 4,
            maxWidth: 160,
            fontSize: 12,
            color: t.chipText,
            lineHeight: 1.35,
            boxShadow: d ? "0 2px 12px rgba(0,0,0,0.2)" : "0 1px 6px rgba(0,0,0,0.08)",
            transition: "opacity 0.25s, color 0.3s, background 0.3s, border-color 0.3s",
          }}
        >
          {ROBOT_MESSAGES[robotMessageIndex]}
          <div
            style={{
              position: "absolute",
              left: 20,
              bottom: -6,
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: `6px solid ${t.chipBg}`,
            }}
          />
        </div>
        <div style={{ animation: "robotFloat 2.5s ease-in-out infinite" }}>
          <svg
            width="80"
            height="80"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: "block" }}
          >
            {/* Body – torso with panel lines */}
            <rect x="16" y="32" width="32" height="26" rx="5" fill={t.chipBg} stroke={t.chipBorder} strokeWidth="1.8" />
            <line x1="22" y1="40" x2="42" y2="40" stroke={t.chipBorder} strokeWidth="1" opacity="0.8" />
            <line x1="22" y1="46" x2="42" y2="46" stroke={t.chipBorder} strokeWidth="1" opacity="0.8" />
            <line x1="22" y1="52" x2="42" y2="52" stroke={t.chipBorder} strokeWidth="1" opacity="0.8" />
            {/* Center LED */}
            <circle cx="32" cy="45" r="2.5" fill={t.linkColor} opacity="0.9" />
            {/* Shoulder joints */}
            <circle cx="14" cy="34" r="4" fill={t.chipBg} stroke={t.chipBorder} strokeWidth="1.5" />
            <circle cx="50" cy="34" r="4" fill={t.chipBg} stroke={t.chipBorder} strokeWidth="1.5" />
            {/* Head – visor housing */}
            <rect x="18" y="8" width="28" height="26" rx="6" fill={t.chipBg} stroke={t.chipBorder} strokeWidth="1.8" />
            {/* Visor – screen/face */}
            <rect x="21" y="14" width="22" height="12" rx="3" fill={t.titleColor} opacity="0.15" stroke={t.chipBorder} strokeWidth="1" />
            <rect x="22" y="15" width="20" height="10" rx="2" fill={t.linkColor} opacity="0.35" />
            {/* Eyes – lit slots */}
            <rect x="24" y="17" width="5" height="3" rx="1" fill={t.titleColor} />
            <rect x="35" y="17" width="5" height="3" rx="1" fill={t.titleColor} />
            {/* Smile line */}
            <path d="M26 24 Q32 27 38 24" stroke={t.subtitleColor} strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.9" />
            {/* Antenna */}
            <line x1="32" y1="8" x2="32" y2="2" stroke={t.chipBorder} strokeWidth="2" strokeLinecap="round" />
            <circle cx="32" cy="1" r="2.5" fill={t.subtitleColor} />
            <circle cx="32" cy="1" r="1" fill={t.linkColor} opacity="0.8" />
          </svg>
        </div>
      </div>

      {/* ── Input ── */}
      <div style={{
        position:"fixed", bottom:0, left:0, right:0, zIndex:10,
        background:t.inputAreaBg, backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
        borderTop:`1px solid ${t.headerBorder}`, padding:"12px 16px 18px",
        transition:"background 0.4s",
      }}>
        <div style={{
          maxWidth:760, margin:"0 auto",
          background:t.inputBoxBg, border:`1px solid ${t.inputBoxBorder}`,
          borderRadius:14, padding:"11px 14px", display:"flex", gap:10, alignItems:"flex-end",
          boxShadow: d ? "0 4px 24px rgba(0,0,0,0.35)" : "0 1px 6px rgba(0,0,0,0.07)",
          transition:"background 0.3s, border-color 0.3s",
        }}>
          <textarea value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();} }}
            placeholder={kbStatus==="loading" ? "Loading knowledge base…" : "Ask about carriers, contracts, tickets…"}
            disabled={inputDisabled} rows={1}
            style={{
              flex:1, border:"none", outline:"none", resize:"none",
              fontSize:14, color:t.inputText, lineHeight:1.55,
              fontFamily:"inherit", background:"transparent",
              opacity: inputDisabled ? 0.5 : 1, transition:"color 0.3s",
            }}
          />
          <button onClick={() => sendMessage()} disabled={inputDisabled||!input.trim()}
            style={{
              background:"#2563eb", color:"#fff", border:"none", borderRadius:9,
              width:34, height:34, fontSize:16, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              opacity: inputDisabled||!input.trim() ? 0.35 : 1, flexShrink:0,
              transition:"opacity 0.2s, transform 0.15s",
            }}
            onMouseEnter={e => { if(!inputDisabled&&input.trim()) e.currentTarget.style.transform="scale(1.1)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; }}
            onMouseDown={e => { if(!inputDisabled&&input.trim()) e.currentTarget.style.transform="scale(0.95)"; }}
            onMouseUp={e => { e.currentTarget.style.transform="scale(1)"; }}
          >↑</button>
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%,80%,100%{opacity:.3;transform:scale(.8)} 40%{opacity:1;transform:scale(1)} }
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes messageIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes chipIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes readyPillIn { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
        @keyframes robotFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        @keyframes gradientShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes orbFloat {
          0%  { transform: translate(0px, 0px)   scale(1);    }
          33% { transform: translate(35px,-45px)  scale(1.06); }
          66% { transform: translate(-25px,22px)  scale(0.96); }
          100%{ transform: translate(0px, 0px)   scale(1);    }
        }
        textarea::placeholder { color: ${t.placeholderC}; transition: color 0.3s; }
        * { box-sizing: border-box; }
        @media (max-width:640px){ .robot-agent-wrap { display: none !important; } }
      `}</style>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Dot({ color }) {
  return <span style={{ width:6, height:6, borderRadius:"50%", background:color, display:"inline-block", flexShrink:0 }} />;
}

function HeaderBtn({ onClick, disabled, title, t, children }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      style={{
        display:"flex", alignItems:"center", gap:6,
        background: disabled ? (t.headerBg) : "transparent",
        border:`1px solid ${t.headerBorder}`,
        borderRadius:8, padding:"6px 12px",
        fontSize:12, fontWeight:500, color: t.subtitleColor,
        cursor: disabled ? "not-allowed" : "pointer",
        transition:"all 0.2s", flexShrink:0, fontFamily:"inherit",
      }}
      onMouseEnter={e => { if(!disabled){e.currentTarget.style.borderColor="#7eb8f7";e.currentTarget.style.color="#7eb8f7";} }}
      onMouseLeave={e => { e.currentTarget.style.borderColor=t.headerBorder; e.currentTarget.style.color=t.subtitleColor; }}
    >
      {children}
    </button>
  );
}
